const LOOKUP_URL = '/api/lookup.php';

export interface VinManager {
  bitrix_id: number;
  name?: string | null;
}

export type VinData = string | Record<string, unknown>;

export interface VinSuccess {
  ok: true;
  vin: string;
  manager: VinManager | null;
  /** Bitrix DIALOG_ID чата, например "chat12535". Присутствует только в ответе lookup. */
  chat?: string | null;
  /** Код контрагента 1С — используется сервером для маршрутизации в чат по таблице «Контрагенты». */
  counterparty?: string | null;
  data: VinData | null;
  /** true — уведомление отправлено; false — отправка не удалась; null — ещё не отправлялось. */
  notified: boolean | null;
  /** 'bitrix' — ушло в Bitrix; 'none' — отправка была, но получателя нет; null — ещё не отправляли (lookup). */
  notified_via: 'bitrix' | 'none' | null;
  /** Детали ошибки доставки в Bitrix, если notified === false. */
  notify_error?: { code: string; message: string } | null;
}

export interface VinFailure {
  ok: false;
  error: { code: string; message: string };
  /** Данные из 1С для отправки в Bitrix при подтверждении VIN пользователем. */
  fallback?: unknown;
}

export type VinResult = VinSuccess | VinFailure;

export async function confirmVin(
  vin: string,
  initData: string,
  fallback: unknown,
  phone?: string | null,
  comment?: string | null,
  repeat?: boolean,
): Promise<VinResult> {
  const res = await fetch('/api/confirm.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({
      vin, initData, fallback,
      ...(phone ? { phone } : {}),
      ...(comment ? { comment } : {}),
      ...(repeat ? { repeat: true } : {}),
    }),
  });
  return (await res.json()) as VinResult;
}

export interface ProfileResult {
  ok: boolean;
  phone: string | null;
}

export interface HistoryItem {
  id: number;
  /** Человекочитаемый номер обращения, например 123. */
  request_no?: number | null;
  /** request_id (16 hex) — для отправки обратной связи. */
  request_id: string;
  vin: string;
  created_at: string;
  comment: string | null;
  /** 'new' — отправлен; 'in_progress' — взят в работу; 'done' — обработан. */
  status: string;
  /** Ответ клиента на «Ваша проблема решена?» — 'resolved'/'unresolved'/null. */
  feedback: string | null;
}

export interface HistoryResult {
  ok: boolean;
  items: HistoryItem[];
}

/** История обращений текущего пользователя (по max_user_id из initData). */
export async function fetchHistory(initData: string): Promise<HistoryResult> {
  const res = await fetch('/api/history.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  return (await res.json()) as HistoryResult;
}

/** Отправляет ответ клиента на вопрос «Ваша проблема решена?». */
export async function sendFeedback(initData: string, requestId: string, answer: 'resolved' | 'unresolved'): Promise<{ ok: boolean; recorded: boolean }> {
  const res = await fetch('/api/feedback.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, request_id: requestId, answer }),
  });
  return (await res.json()) as { ok: boolean; recorded: boolean };
}

/** Скрывает запись из истории пользователя (мягкое удаление). Возвращает обновлённый список. */
export async function deleteHistory(initData: string, id: number): Promise<HistoryResult> {
  const res = await fetch('/api/history.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, delete_id: id }),
  });
  return (await res.json()) as HistoryResult;
}

/** Запрашивает сохранённый телефон пользователя по initData (узнаём по max_user_id). */
export async function fetchProfile(initData: string): Promise<ProfileResult> {
  const res = await fetch('/api/profile.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData }),
  });
  return (await res.json()) as ProfileResult;
}

/** Сохраняет телефон пользователя (после первого requestContact). */
export async function saveProfile(initData: string, phone: string): Promise<ProfileResult> {
  const res = await fetch('/api/profile.php', {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ initData, phone }),
  });
  return (await res.json()) as ProfileResult;
}

export async function lookupVin(
  vin: string,
  initData: string,
): Promise<VinResult> {
  const res = await fetch(LOOKUP_URL, {
    method: 'POST',
    headers: { 'content-type': 'application/json' },
    body: JSON.stringify({ vin, initData }),
  });
  return (await res.json()) as VinResult;
}
